// src/pages/LectureFaqPage.js
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageContainer from '../components/Layout/PageContainer';
import SeoHead from '../components/SeoHead';
import { useLanguage } from '../i18n/LanguageContext';
import LectureFaqMenu from '../components/LectureFaqMenu';
import '../styles/lectureUpload.css';

const T = {
  en: {
    headTitle:
      'Lecture STT FAQ | How It Works · Recording & Upload · Languages · Pricing · Privacy',
    headDesc:
      'Frequently asked questions for Lecture STT: how the service works, supported formats, recording & upload tips, language and auto-detect, pricing, privacy, and troubleshooting.',
    pageTitle: 'Lecture STT — FAQ',
    intro: (
      <>
        Here you can find answers about how Lecture STT works, recording & upload,
        supported languages, pricing plans, privacy, and troubleshooting.
      </>
    ),
    lastUpdated: 'Last updated: 02 Dec 2025 (Beta)',

    // Left menu labels (same as How-to-Use page / shared menu)
    faqMenuTitle: 'STT FAQ Menu',
    faqMenuHowToUse: 'How to Use',
    faqMenuTips: 'Recording & Upload Tips',
    faqMenuLanguage: 'Supported Languages',
    faqMenuPrivacy: 'Privacy & Data',
    faqMenuVersion: 'Version & Updates',
    faqMenuSupport: 'Support / Contact',
    faqMenuAll: 'View all STT FAQs',

    // Section titles
    secOverview: 'Overview',
    secRecord: 'Recording & Upload',
    secLang: 'Languages',
    secPricing: 'Pricing & Plans',
    secPrivacy: 'Privacy & Data',
    secTrouble: 'Troubleshooting',

    // Overview
    o1q: 'What is Lecture STT?',
    o1a: (
      <>
        Lecture STT helps you turn <strong>lecture or meeting recordings</strong> into
        searchable transcripts, plus optional AI summaries. You can record with our
        Chrome extension or upload an existing audio file, then check the result on the
        <strong> Status</strong> page.
      </>
    ),
    o2q: 'Is Lecture STT free?',
    o2a: (
      <>
        During the initial <strong>beta</strong> period, the service is available as a
        Free plan with fair-use limits. We may introduce paid plans later.
      </>
    ),
    o3q: 'Do I need an account?',
    o3a: (
      <>
        Yes. Please sign in with <strong>Google</strong>. We use your account so that
        your uploaded recordings and transcripts are saved under your account and only
        accessible to you.
      </>
    ),
    o4extra: (
      <>
        New here? See{' '}
        <Link to="/lecture/faq/how-to-use" className="faq-link">
          How to Use STT Lecture Transcription
        </Link>{' '}
        for a step-by-step guide.
      </>
    ),

    // Recording & upload
    r1q: 'How do I record my lecture?',
    r1a: (
      <>
        You can either use our <strong>Chrome extension</strong> (microphone or tab
        recording) during the class, or record using your own tool (phone recorder,
        Zoom recording, LMS playback, etc.) and upload the audio file later.
      </>
    ),
    r2q: 'Which file formats are supported?',
    r2a: (
      <>
        We currently support <code>.webm</code>, <code>.m4a</code>, <code>.mp3</code>,
        and <code>.wav</code>. Other formats may be added later.
      </>
    ),
    r3q: 'Is there a limit on file length or size?',
    r3a: (
      <>
        Very long recordings may take longer to upload and process, and may be less
        stable. For best results, we suggest splitting extremely long classes into
        parts of around <strong>60–120 minutes</strong> each. Additional limits (for
        example, file size) will be shown on the Upload page if applicable.
      </>
    ),
    r4q: 'What happens after I upload?',
    r4a: (
      <>
        After you click <strong>Upload / Generate</strong>, your file is queued and
        processed. You can monitor progress on the{' '}
        <Link to="/lecture/status" className="faq-link">
          Status
        </Link>{' '}
        page, and open the lecture detail once it’s complete.
      </>
    ),

    // Languages
    l1q: 'Which languages are supported?',
    l1a: (
      <>
        We plan to support multiple languages commonly used in lectures (for example
        English, Mandarin, Cantonese). The exact list may change as we adjust the
        service. On the Upload page you can see the current supported language options.
      </>
    ),
    l2q: 'Can it auto-detect language?',
    l2a: (
      <>
        Yes. By default, you can choose <strong>Auto</strong> so the system tries to
        detect the main language in your recording. If the result is not ideal, you can
        switch to <strong>manual selection</strong> and pick a specific language or
        locale.
      </>
    ),
    l3q: 'Can I mix multiple languages in one recording?',
    l3a: (
      <>
        Mixed languages are possible, but recognition quality may drop if the speaker
        frequently switches language. For the best accuracy, try to keep{' '}
        <strong>one main language per recording</strong> when you can.
      </>
    ),

    // Pricing
    p1q: 'How is usage counted?',
    p1a: (
      <>
        We may track usage based on <strong>audio duration</strong> and/or the number of
        characters in the transcript. During beta, we focus on stability and may adjust
        limits over time.
      </>
    ),
    p2q: 'Are there paid plans?',
    p2a: (
      <>
        <strong>Not yet.</strong> We may introduce paid plans later (for example,
        higher limits or team usage). Pricing details are not finalised and will be
        announced separately.
      </>
    ),
    p3q: 'Will my existing transcripts remain if pricing changes?',
    p3a: (
      <>
        Our goal is to keep your existing transcripts accessible as much as possible,
        even if pricing changes. If any migration or cleanup is needed in the future,
        we will announce it clearly in advance.
      </>
    ),

    // Privacy
    pr1q: 'Do you store my audio and transcripts?',
    pr1a: (
      <>
        Your recordings and transcripts are stored in our cloud storage under your
        account for processing and later access. Please avoid uploading content that
        violates local laws or our policies.
      </>
    ),
    pr2q: 'How long will my data be kept?',
    pr2a: (
      <>
        During beta, we may keep data for a period of time to support re-downloads and
        service tuning. Retention rules may evolve, and we will update this FAQ when
        any major change happens.
      </>
    ),
    pr3q: 'Is it safe to upload sensitive content?',
    pr3a: (
      <>
        We recommend <strong>not</strong> uploading highly sensitive personal data. If
        your lecture contains confidential information, please review and use the
        service carefully, or consider anonymising content where possible.
      </>
    ),

    // Troubleshooting
    t1q: 'My job stays in “Processing” for a long time — what should I do?',
    t1a: (
      <>
        For very long recordings, processing can take longer. If a job is stuck for an
        unusually long time, try refreshing the Status page after a few minutes. If it
        still does not finish, please try uploading again later or contact us with the
        Lecture ID.
      </>
    ),
    t2q: 'The status shows “Failed” — can I retry?',
    t2a: (
      <>
        Yes. You can usually re-upload the same recording and try again. If it fails
        repeatedly, please check the file format and size, or try exporting your audio
        again from the original tool.
      </>
    ),
    t3q: 'Upload error or 502 issue?',
    t3a: (
      <>
        Temporary network or server issues can cause upload failures. Please refresh and
        try again. If the issue continues, kindly capture a screenshot and your Lecture
        ID and contact us.
      </>
    ),

    needHelp: (
      <>
        Need more help? You can reach us via the{' '}
        <Link to="/lecture/faq/support" className="faq-link">
          Support / Contact
        </Link>{' '}
        page on the main site.
      </>
    ),
  },

  // Traditional Chinese
  'zh-Hant': {
    headTitle: 'Lecture STT 常見問題｜運作方式・錄音與上載・支援語言・收費・隱私',
    headDesc:
      'Lecture STT 的常見問題：服務如何運作、支援格式、錄音與上載小貼士、語言與自動偵測、收費與方案、隱私與資料，以及疑難排解。',
    pageTitle: 'Lecture STT 常見問題',
    intro: (
      <>
        這裡整理了 Lecture STT 的常見問題，包括服務運作方式、錄音與上載、支援語言、
        收費與方案、隱私與資料，以及疑難排解。
      </>
    ),
    lastUpdated: '最後更新：2025 年 12 月 2 日（Beta）',

    faqMenuTitle: 'STT 常見問題選單',
    faqMenuHowToUse: '如何使用',
    faqMenuTips: '錄音與上傳小貼士',
    faqMenuLanguage: '支援語言',
    faqMenuPrivacy: '隱私與資料安全',
    faqMenuVersion: '版本與更新記錄',
    faqMenuSupport: '支援／聯絡我們',
    faqMenuAll: '查看全部 STT 常見問題',

    secOverview: '服務概覽',
    secRecord: '錄音與上載',
    secLang: '語言與自動偵測',
    secPricing: '收費與方案',
    secPrivacy: '隱私與資料',
    secTrouble: '疑難排解',

    o1q: '什麼是 Lecture STT？',
    o1a: (
      <>
        Lecture STT 可以將你的<strong>課堂或會議錄音</strong>
        轉成可搜尋的逐字稿，並可選擇產生 AI 摘要。
        你可以用我們的 Chrome 擴充功能錄音，或上載現有錄音，再到
        <strong>狀態頁</strong>查看結果。
      </>
    ),
    o2q: 'Lecture STT 是否免費？',
    o2a: (
      <>
        在初期<strong>Beta 期間</strong>，以 Free 方案提供，並設有公平使用上限。
        未來有機會推出付費方案。
      </>
    ),
    o3q: '需要登入帳號嗎？',
    o3a: (
      <>
        需要。請使用 <strong>Google 帳號登入</strong>，
        我們會以你的帳號管理已上載的錄音與逐字稿，方便日後查閱。
      </>
    ),
    o4extra: (
      <>
        第一次使用？建議先參考{' '}
        <Link to="/lecture/faq/how-to-use" className="faq-link">
          如何使用 STT 語音轉筆記
        </Link>
        ，一步步完成錄音與上載。
      </>
    ),

    r1q: '我要如何錄製課堂？',
    r1a: (
      <>
        你可以使用我們的 <strong>Chrome 擴充功能</strong>（麥克風或分頁錄音）在上課期間錄音，
        或使用自己慣用的工具（例如手機錄音、Zoom 錄影），課後再上載音訊檔。
      </>
    ),
    r2q: '目前支援哪些檔案格式？',
    r2a: (
      <>
        目前支援 <code>.webm</code>、<code>.m4a</code>、<code>.mp3</code>、
        <code>.wav</code>，日後可能按需要新增其他格式。
      </>
    ),
    r3q: '錄音長度或檔案大小有限制嗎？',
    r3a: (
      <>
        非常長的錄音上載與處理時間會較長，也較容易出現不穩定情況。
        建議將超長課堂切成約<strong>60–120 分鐘</strong>一段，上載與處理會較穩定。
        如有額外限制（例如檔案大小），會在上載頁面清楚標示。
      </>
    ),
    r4q: '上載之後會發生什麼事？',
    r4a: (
      <>
        點擊 <strong>Upload／Generate</strong> 後，系統會將檔案排入處理隊列。
        你可以到{' '}
        <Link to="/lecture/status" className="faq-link">
          狀態頁（Status）
        </Link>
        查看進度，完成後即可打開該講座查看逐字稿與摘要。
      </>
    ),

    l1q: '支援哪些語言？',
    l1a: (
      <>
        我們會逐步支援課堂常用的多種語言（例如英文、國語、粵語等）。
        實際支援清單以上載頁面可選擇的語言為準，未來可能按情況調整。
      </>
    ),
    l2q: '可以自動偵測語言嗎？',
    l2a: (
      <>
        可以。你可以選擇 <strong>Auto（自動偵測）</strong>，
        系統會嘗試判斷錄音中的主要語言。若結果不理想，可以改用
        <strong>Manual（手動）</strong>，直接選擇語言或地區設定。
      </>
    ),
    l3q: '同一段錄音可以混合多種語言嗎？',
    l3a: (
      <>
        可以，但若經常在不同語言之間切換，辨識準確度可能下降。
        建議盡量<strong>每段錄音以一種主要語言為主</strong>，效果會較佳。
      </>
    ),

    p1q: '使用量是怎樣計算的？',
    p1a: (
      <>
        我們可能會根據<strong>音訊長度</strong>及／或逐字稿字數計算使用量。
        Beta 期間主要以穩定性為優先，相關上限可能會隨服務調整。
      </>
    ),
    p2q: '會有付費方案嗎？',
    p2a: (
      <>
        <strong>目前尚未推出。</strong> 將來可能提供付費方案（例如更高上限或團隊使用），
        價格與細節會另行公告。
      </>
    ),
    p3q: '若日後收費，我現有的逐字稿會被刪除嗎？',
    p3a: (
      <>
        我們會盡量保留你現有的逐字稿。如日後因系統調整需要清理或搬遷資料，
        會預先以公告或 FAQ 清楚說明。
      </>
    ),

    pr1q: '你們會儲存我的錄音與逐字稿嗎？',
    pr1a: (
      <>
        系統會將你的錄音與逐字稿儲存在雲端，並以你的帳號歸戶，
        方便之後再次查看與下載。請避免上載違反法律或平台政策的內容。
      </>
    ),
    pr2q: '資料會保存多久？',
    pr2a: (
      <>
        在 Beta 期間，我們可能保留一段時間，以支援重新下載及服務調整。
        資料保存政策若有重大變更，會在此 FAQ 更新。
      </>
    ),
    pr3q: '可以上載含有敏感資料的內容嗎？',
    pr3a: (
      <>
        建議<strong>避免</strong>上載高度敏感的個人資料或機密內容。
        如必須處理相關錄音，請先衡量風險，或盡量在錄音前去識別化（例如移除姓名等）。
      </>
    ),

    t1q: '狀態長時間停留在「Processing」怎麼辦？',
    t1a: (
      <>
        對於錄音較長的檔案，處理時間會相對增加。
        若長時間沒有變化，可先等幾分鐘後重新整理狀態頁；
        如仍然卡住，建議稍後重新上載，或記下 Lecture ID 後與我們聯絡。
      </>
    ),
    t2q: '顯示「Failed」可以重試嗎？',
    t2a: (
      <>
        可以。一般情況下可重新上載同一錄音再試一次。
        若多次失敗，請檢查檔案格式與大小，或從原始工具重新匯出音訊。
      </>
    ),
    t3q: '上載時出現錯誤或 502？',
    t3a: (
      <>
        可能是網路或伺服器暫時問題。請先重新整理並再試一次；
        若問題持續，請截圖及記錄 Lecture ID，以便日後支援時參考。
      </>
    ),

    needHelp: (
      <>
        需要更多協助？你可以透過{' '}
        <Link to="/lecture/faq/support" className="faq-link">
          支援／聯絡我們
        </Link>{' '}
        頁面與我們聯絡。
      </>
    ),
  },

  // Simplified Chinese
  'zh-Hans': {
    headTitle: 'Lecture STT 常见问题｜运作方式・录音与上传・支持语言・收费・隐私',
    headDesc:
      'Lecture STT 的常见问题：服务如何运作、支持格式、录音与上传小贴士、语言与自动检测、收费与方案、隐私与数据，以及疑难排解。',
    pageTitle: 'Lecture STT 常见问题',
    intro: (
      <>
        这里整理了 Lecture STT 的常见问题，包括服务运作方式、录音与上传、支持语言、
        收费与方案、隐私与数据，以及疑难排解。
      </>
    ),
    lastUpdated: '最后更新：2025 年 12 月 2 日（Beta）',

    faqMenuTitle: 'STT 常见问题菜单',
    faqMenuHowToUse: '如何使用',
    faqMenuTips: '录音与上传小贴士',
    faqMenuLanguage: '支持语言',
    faqMenuPrivacy: '隐私与数据安全',
    faqMenuVersion: '版本与更新记录',
    faqMenuSupport: '客服／联系我们',
    faqMenuAll: '查看全部 STT 常见问题',

    secOverview: '服务概览',
    secRecord: '录音与上传',
    secLang: '语言与自动检测',
    secPricing: '收费与方案',
    secPrivacy: '隐私与数据',
    secTrouble: '疑难排解',

    o1q: '什么是 Lecture STT？',
    o1a: (
      <>
        Lecture STT 可以将你的<strong>课堂或会议录音</strong>转换成可搜索的逐字稿，
        并可选择生成 AI 摘要。你可以用我们的 Chrome 扩展录音，或上传现有录音，
        然后到<strong>状态页</strong>查看结果。
      </>
    ),
    o2q: 'Lecture STT 是否免费？',
    o2a: (
      <>
        在初期<strong>Beta 期间</strong>，以 Free 方案提供，并设有公平使用上限。
        未来有机会推出付费方案。
      </>
    ),
    o3q: '需要登录帐号吗？',
    o3a: (
      <>
        需要。请使用 <strong>Google 帐号登录</strong>，我们会以你的帐号管理已上传的录音
        与逐字稿，方便之后查看。
      </>
    ),
    o4extra: (
      <>
        第一次使用？建议先参考{' '}
        <Link to="/lecture/faq/how-to-use" className="faq-link">
          如何使用 STT 语音转笔记
        </Link>
        ，一步步完成录音与上传。
      </>
    ),

    r1q: '我要如何录制课堂？',
    r1a: (
      <>
        你可以使用我们的 <strong>Chrome 扩展</strong>（麦克风或标签页录音）在上课期间录音，
        或使用自己常用的工具（如手机录音、Zoom 录音），课后再上传音频文件。
      </>
    ),
    r2q: '目前支持哪些文件格式？',
    r2a: (
      <>
        目前支持 <code>.webm</code>、<code>.m4a</code>、<code>.mp3</code>、
        <code>.wav</code>，将来可能按需要增加其他格式。
      </>
    ),
    r3q: '录音长度或文件大小有限制吗？',
    r3a: (
      <>
        非常长的录音上传与处理时间会更长，也比较容易出现不稳定情况。
        建议将超长课程拆成约<strong>60–120 分钟</strong>一段，上传与处理会更稳定。
        如有额外限制（例如文件大小），会在上传页面清楚标示。
      </>
    ),
    r4q: '上传之后会发生什么？',
    r4a: (
      <>
        点击 <strong>Upload／Generate</strong> 后，系统会将文件排入队列进行处理。
        你可以到{' '}
        <Link to="/lecture/status" className="faq-link">
          状态页（Status）
        </Link>
        查看进度，完成后即可打开该课堂查看逐字稿与摘要。
      </>
    ),

    l1q: '支持哪些语言？',
    l1a: (
      <>
        我们会逐步支持课堂常用的多种语言（如英语、普通话、粤语等）。
        实际支持列表以上传页面可选择的语言为准，未来可能根据情况调整。
      </>
    ),
    l2q: '可以自动检测语言吗？',
    l2a: (
      <>
        可以。你可以选择 <strong>Auto（自动检测）</strong>，
        系统会尝试判断录音中的主要语言。若结果不理想，可以改用
        <strong>Manual（手动）</strong>，直接选择语言或地区设置。
      </>
    ),
    l3q: '同一段录音可以混合多种语言吗？',
    l3a: (
      <>
        可以，但如果经常在不同语言之间切换，识别准确度可能下降。
        建议尽量<strong>每段录音以一种主要语言为主</strong>，效果会更好。
      </>
    ),

    p1q: '使用量是怎样计算的？',
    p1a: (
      <>
        我们可能会根据<strong>音频时长</strong>及／或逐字稿字数计算使用量。
        Beta 期间主要以稳定性为优先，相关上限可能会随着服务调整。
      </>
    ),
    p2q: '会有付费方案吗？',
    p2a: (
      <>
        <strong>目前尚未推出。</strong> 将来可能提供付费方案（例如更高上限或团队使用），
        价格与细节会另行公告。
      </>
    ),
    p3q: '如果将来开始收费，我现有的逐字稿会被删除吗？',
    p3a: (
      <>
        我们会尽量保留你现有的逐字稿。若日后因系统调整需要清理或迁移数据，
        会提前通过公告或 FAQ 清楚说明。
      </>
    ),

    pr1q: '你们会保存我的录音与逐字稿吗？',
    pr1a: (
      <>
        系统会将你的录音与逐字稿存储在云端，并按帐号归属，
        方便之后再次查看与下载。请避免上传违反法律或平台政策的内容。
      </>
    ),
    pr2q: '数据会保存多久？',
    pr2a: (
      <>
        在 Beta 期间，我们可能保留一段时间，以支持重新下载及服务调整。
        如果数据保存政策有重大变更，会在本 FAQ 中更新。
      </>
    ),
    pr3q: '可以上传含有敏感数据的内容吗？',
    pr3a: (
      <>
        建议<strong>避免</strong>上传高度敏感的个人信息或机密内容。
        如必须处理相关录音，请先评估风险，或尽量在录音前去标识化（如移除姓名等）。
      </>
    ),

    t1q: '状态长时间停留在“Processing”怎么办？',
    t1a: (
      <>
        对于录音较长的文件，处理时间会相对增加。
        若长时间没有变化，可以先等几分钟后刷新状态页；
        若仍然卡住，建议稍后重新上传，或记录 Lecture ID 后与我们联系。
      </>
    ),
    t2q: '显示“Failed”可以重试吗？',
    t2a: (
      <>
        可以。一般情况下可以重新上传同一录音再试一次。
        若多次失败，请检查文件格式与大小，或从原始工具重新导出音频。
      </>
    ),
    t3q: '上传时出现错误或 502？',
    t3a: (
      <>
        可能是网络或服务器暂时问题。请先刷新并再次尝试；
        若问题持续，请截图并记录 Lecture ID，方便日后支持时参考。
      </>
    ),

    needHelp: (
      <>
        需要更多协助？你可以通过{' '}
        <Link to="/lecture/faq/support" className="faq-link">
          客服／联系我们
        </Link>{' '}
        页面与我们联系。
      </>
    ),
  },
};


function detectUiLocale(navUploadLabel) {
  if (!navUploadLabel) return 'en';
  if (navUploadLabel === 'Upload') return 'en';
  if (navUploadLabel.includes('上传')) return 'zh-Hans';
  if (/[\u4e00-\u9fff]/.test(navUploadLabel)) return 'zh-Hant';
  return 'en';
}

const LectureFaqPage = () => {
  const { t } = useLanguage();
  const navUploadLabel = t ? t('navUpload') : 'Upload';
  const uiLocale = detectUiLocale(navUploadLabel);
  const strings = useMemo(() => T[uiLocale] || T.en, [uiLocale]);
  const isEn = uiLocale === 'en';

  const url = 'https://stt.aittsservice.online/lecture/faq';

  const breadcrumbs = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: isEn ? 'Lecture STT Service' : 'Lecture STT 服務',
          item: 'https://stt.aittsservice.online/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: isEn ? 'FAQ' : '常見問題',
          item: url,
        },
      ],
    },
  ];

  return (
    <PageContainer>
      <SeoHead
        title={strings.headTitle}
        description={strings.headDesc}
        url={url}
        structuredData={breadcrumbs}
      />

      {/* Hero – same style as How-to-Use */}
      <div className="card upload-hero">
        <div className="card-heading">
          <h1 className="card-title">{strings.pageTitle}</h1>
        </div>
        <p>{strings.intro}</p>
        <p style={{ fontSize: 12, marginTop: 4 }}>{strings.lastUpdated}</p>
      </div>

      {/* Main content with same 2-column layout as How-to-Use */}
      <div className="card upload-card upload-faq-card">
        <div className="upload-layout upload-layout--faq howto-grid">
          <LectureFaqMenu active="faq" />

          {/* Right: FAQ sections */}
          <main className="upload-main upload-main--faq howto-main">
            {/* Overview */}
            <section className="faq-section">
              <h2>{strings.secOverview}</h2>
              <ul>
                <li>
                  <strong>{strings.o1q}</strong>
                  <div>{strings.o1a}</div>
                </li>
                <li>
                  <strong>{strings.o2q}</strong>
                  <div>{strings.o2a}</div>
                </li>
                <li>
                  <strong>{strings.o3q}</strong>
                  <div>{strings.o3a}</div>
                </li>
              </ul>
              <p style={{ marginTop: 8 }}>{strings.o4extra}</p>
            </section>

            {/* Recording & upload */}
            <section className="faq-section">
              <h2>{strings.secRecord}</h2>
              <ul>
                <li>
                  <strong>{strings.r1q}</strong>
                  <div>{strings.r1a}</div>
                </li>
                <li>
                  <strong>{strings.r2q}</strong>
                  <div>{strings.r2a}</div>
                </li>
                <li>
                  <strong>{strings.r3q}</strong>
                  <div>{strings.r3a}</div>
                </li>
                <li>
                  <strong>{strings.r4q}</strong>
                  <div>{strings.r4a}</div>
                </li>
              </ul>
            </section>

            {/* Languages */}
            <section className="faq-section">
              <h2>{strings.secLang}</h2>
              <ul>
                <li>
                  <strong>{strings.l1q}</strong>
                  <div>{strings.l1a}</div>
                </li>
                <li>
                  <strong>{strings.l2q}</strong>
                  <div>{strings.l2a}</div>
                </li>
                <li>
                  <strong>{strings.l3q}</strong>
                  <div>{strings.l3a}</div>
                </li>
              </ul>
            </section>

            {/* Pricing */}
            <section className="faq-section">
              <h2>{strings.secPricing}</h2>
              <ul>
                <li>
                  <strong>{strings.p1q}</strong>
                  <div>{strings.p1a}</div>
                </li>
                <li>
                  <strong>{strings.p2q}</strong>
                  <div>{strings.p2a}</div>
                </li>
                <li>
                  <strong>{strings.p3q}</strong>
                  <div>{strings.p3a}</div>
                </li>
              </ul>
            </section>

            {/* Privacy */}
            <section className="faq-section">
              <h2>{strings.secPrivacy}</h2>
              <ul>
                <li>
                  <strong>{strings.pr1q}</strong>
                  <div>{strings.pr1a}</div>
                </li>
                <li>
                  <strong>{strings.pr2q}</strong>
                  <div>{strings.pr2a}</div>
                </li>
                <li>
                  <strong>{strings.pr3q}</strong>
                  <div>{strings.pr3a}</div>
                </li>
              </ul>
            </section>

            {/* Troubleshooting */}
            <section className="faq-section">
              <h2>{strings.secTrouble}</h2>
              <ul>
                <li>
                  <strong>{strings.t1q}</strong>
                  <div>{strings.t1a}</div>
                </li>
                <li>
                  <strong>{strings.t2q}</strong>
                  <div>{strings.t2a}</div>
                </li>
                <li>
                  <strong>{strings.t3q}</strong>
                  <div>{strings.t3a}</div>
                </li>
              </ul>
            </section>

            <section className="faq-section">
              <h2>Help</h2>
              <p>{strings.needHelp}</p>
            </section>
          </main>
        </div>
      </div>
    </PageContainer>
  );
};

export default LectureFaqPage;
